import React from 'react';
import type {LucideIcon} from 'lucide-react';
import type {StatusTone, StatusToneStyles} from './statusTone';

export interface ProviderNotification {
    id: number;
    title: string;
    detail: string;
    cta: string;
    tone: StatusTone;
    icon: LucideIcon;
}

export interface NotificationsPanelProps {
    notifications: ProviderNotification[];
    statusToneStyles: StatusToneStyles;
}

const NotificationsPanel: React.FC<NotificationsPanelProps> = ({notifications, statusToneStyles}) => {
    return (
        <div>
            <div className="flex items-center justify-between">
                <h2 className="text-[27px] font-medium text-mld-text-primary mb-2 ">Notifications</h2>
                <button className="text-mld-muted-text hover:text-[#42D4B0]" type="button">
                    View All
                </button>
            </div>
            <div className="grid grid-cols-[1fr_100px] rounded-2xl border border-mld-panel-border bg-mld-menu-background">
                {notifications.length === 0 ? (
                    <div className="col-span-2 flex items-center justify-center px-6 py-8 text-sm text-mld-muted-text bg-mld-background rounded-2xl">
                        No notifications right now.
                    </div>
                ) : (
                    notifications.map((notification, index) => {
                        const isFirst = index === 0;
                        const isLast = index === notifications.length - 1;
                        const NotificationIcon = notification.icon;

                        const rowClasses = `
                                ${index % 2 === 0 ? 'bg-[#f6f8f2]' : 'bg-white'}
                                ${index !== notifications.length - 1 ? 'border-b border-mld-panel-border ' : ''}
                            `.trim();
                        return (
                            <React.Fragment key={notification.id}>
                                {/* Left cell */}
                                <div
                                    className={`flex items-center p-4 gap-4 border-r border-mld-panel-border 
                                    ${rowClasses} 
                                    ${isFirst ? 'rounded-tl-2xl' : ''}
                                    ${isLast ? 'rounded-bl-2xl' : ''}`}>
                                    <NotificationIcon
                                        aria-hidden="true"
                                        className={`h-4 w-4 shrink-0 ${statusToneStyles[notification.tone].action}`}
                                    />
                                    <div className="min-w-0">
                                        <p className="ParagraphNormalBold text-mld-text-primary">
                                            {notification.title}
                                        </p>
                                        <p className="ParagraphSmallRegular text-mld-text-secondary">
                                            {notification.detail}
                                        </p>
                                    </div>
                                </div>

                                {/* Right cell */}
                                <div
                                    className={`flex items-center justify-center 
                                    ${rowClasses}
                                    ${isFirst ? 'rounded-tr-2xl' : ''}
                                    ${isLast ? 'rounded-br-2xl' : ''}`}>
                                    <button
                                        type="button"
                                        aria-label={`${notification.cta}: ${notification.title}`}
                                        className="rounded-full border border-black h-8 px-4 py-1.5 text-xs">
                                        {notification.cta}
                                    </button>
                                </div>
                            </React.Fragment>
                        );
                    })
                )}
            </div>
        </div>
    );
};

export default NotificationsPanel;
